import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import DeleteCaseButton from "@/components/DeleteCaseButton";
import { toast } from "sonner";
import { Copy, Loader2, RefreshCw, FileSearch, AlertTriangle, ShieldAlert, Target, Scale, FileCheck2, Gavel, ArrowLeft, Save, MessageSquareReply, ListChecks } from "lucide-react";
import { api, errMsg, STYLES, STATUSES, TONES, REPLY_TONES, RESPONSIBILITY, FINAL_REC, OCCURRENCES, DELIVERY, YESNO, fmtDate, caseTitle, charLimit } from "@/lib/api";
import { Panel, StrengthStars, StatusBadge, PlatformBadge, AuthImage, ScoreRing, FillGuide } from "@/components/Shared";
import { SentimentChip } from "@/components/ReplyResult";
import { Field, Select, inputCls, areaCls, btnGhost, btnPrimary } from "@/components/Form";

const REC_CLS = { contestar: "bg-emerald-600", reunir_mais_provas: "bg-amber-500 text-slate-900", nao_contestar: "bg-red-600" };
const ALEG_CLS = { contradita: "text-emerald-700 bg-emerald-50", confirmada: "text-red-700 bg-red-50", nao_comprovavel: "text-slate-600 bg-slate-100", parcial: "text-amber-800 bg-amber-50" };

const List = ({ items, empty = "Nenhum item.", testid }) => (
  items?.length ? <ul className="space-y-2" data-testid={testid}>{items.map((x) => <li key={x} className="flex gap-2 text-sm text-slate-700"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />{x}</li>)}</ul>
    : <p className="text-sm text-slate-400">{empty}</p>
);

function ContestEditor({ c, setC, limit, styles = STYLES, title = "Texto pronto para contestação", regenLabel = "Regenerar no estilo" }) {
  const [busy, setBusy] = useState("");
  const [text, setText] = useState(c.contest_text || "");
  useEffect(() => setText(c.contest_text || ""), [c.contest_text]);
  const regen = async (style) => {
    setBusy(style);
    try {
      const { data } = await api.post(`/cases/${c.case_id}/regenerate`, { style });
      setC({ ...c, contest_text: data.contest_text });
      toast.success(`Texto reescrito: ${styles[style]}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const save = async () => {
    const { data } = await api.patch(`/cases/${c.case_id}`, { contest_text: text });
    setC(data); toast.success("Texto salvo");
  };
  const copy = async () => { await navigator.clipboard.writeText(text); toast.success("Texto copiado! Cole na plataforma."); };
  const over = text.length >= limit;
  return (
    <Panel title={title} icon={FileCheck2} testid="contest-text-panel" className="border-amber-300 ring-1 ring-amber-200">
      <textarea data-testid="contest-text-area" rows={6} maxLength={limit} value={text} onChange={(e) => setText(e.target.value.slice(0, limit))} className={`${areaCls} font-medium leading-relaxed`} />
      <div className="mt-2 flex items-center justify-between text-xs">
        <span className="text-slate-400">Limite da plataforma para {OCCURRENCES[c.occurrence_type]?.toLowerCase()}</span>
        <span data-testid="char-counter" className={`font-mono font-bold ${over ? "text-red-600" : "text-slate-600"}`}>{text.length}/{limit}</span>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <button data-testid="copy-contest-btn" onClick={copy} className="inline-flex h-12 items-center gap-2 rounded-lg bg-amber-500 px-6 font-extrabold text-white transition-colors hover:bg-amber-600"><Copy className="h-5 w-5" /> COPIAR</button>
        <button data-testid="save-contest-btn" onClick={save} disabled={text === c.contest_text} className={btnGhost}><Save className="h-4 w-4" /> Salvar edição</button>
      </div>
      <p className="mb-2 mt-6 text-xs font-bold uppercase tracking-widest text-slate-400">{regenLabel}</p>
      <div className="flex flex-wrap gap-2">
        {Object.entries(styles).map(([k, v]) => (
          <button key={k} data-testid={`regen-${k}`} disabled={!!busy} onClick={() => regen(k)} className={`${btnGhost} ${k === "mais_forte" ? "border-slate-900 font-bold text-slate-900" : ""}`}>
            {busy === k ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} {v}
          </button>
        ))}
      </div>
    </Panel>
  );
}

function TrackingPanel({ c, setC, cats }) {
  const [f, setF] = useState({ status: c.status, chosen_motivo: c.chosen_motivo, chosen_submotivo: c.chosen_submotivo, financial_value: c.financial_value, recovered_value: c.recovered_value, platform_result: c.platform_result });
  const subs = cats.find((x) => x.motivo === f.chosen_motivo)?.submotivos || [];
  const save = async () => {
    try {
      const { data } = await api.patch(`/cases/${c.case_id}`, { ...f, financial_value: Number(f.financial_value || 0), recovered_value: Number(f.recovered_value || 0) });
      setC(data); toast.success("Recurso atualizado");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title="Acompanhamento do recurso" icon={ListChecks} testid="tracking-panel">
      <div className="space-y-4">
        <Field label="Status"><Select testid="status-select" options={STATUSES} value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} /></Field>
        <Field label="Motivo selecionado"><Select testid="chosen-motivo-select" options={Object.fromEntries([[f.chosen_motivo, f.chosen_motivo], ...cats.map((x) => [x.motivo, x.motivo])])} value={f.chosen_motivo} onChange={(e) => setF({ ...f, chosen_motivo: e.target.value, chosen_submotivo: "" })} /></Field>
        {subs.length > 0 && <Field label="Submotivo selecionado"><Select testid="chosen-submotivo-select" options={Object.fromEntries([["", "—"], ...subs.map((s) => [s, s])])} value={f.chosen_submotivo} onChange={(e) => setF({ ...f, chosen_submotivo: e.target.value })} /></Field>}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Valor envolvido"><input data-testid="track-financial-input" type="number" step="0.01" className={inputCls} value={f.financial_value} onChange={(e) => setF({ ...f, financial_value: e.target.value })} /></Field>
          <Field label="Valor recuperado" hint="Só conta após aprovação"><input data-testid="track-recovered-input" type="number" step="0.01" className={inputCls} value={f.recovered_value} onChange={(e) => setF({ ...f, recovered_value: e.target.value })} /></Field>
        </div>
        <Field label="Resultado informado pela plataforma"><textarea data-testid="platform-result-input" rows={2} className={areaCls} value={f.platform_result} onChange={(e) => setF({ ...f, platform_result: e.target.value })} /></Field>
        <button data-testid="save-tracking-btn" onClick={save} className={`${btnPrimary} w-full`}><Save className="h-4 w-4" /> Salvar acompanhamento</button>
      </div>
    </Panel>
  );
}

function CaseReply({ c, setC }) {
  const [tone, setTone] = useState("profissional");
  const [busy, setBusy] = useState(false);
  const gen = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/public-reply", { review_text: c.customer_comment || "(sem comentário)", tone, store_id: c.store_id, case_id: c.case_id });
      setC({ ...c, public_replies: [...(c.public_replies || []), { tone, text: data.reply }] });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const last = c.public_replies?.[c.public_replies.length - 1];
  return (
    <Panel title="Resposta pública ao cliente" icon={MessageSquareReply} testid="case-reply-panel">
      <div className="flex gap-2">
        <Select testid="case-reply-tone" options={TONES} value={tone} onChange={(e) => setTone(e.target.value)} />
        <button data-testid="case-reply-generate-btn" onClick={gen} disabled={busy} className={btnPrimary}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Gerar"}</button>
      </div>
      {last && (
        <div className="mt-4 rounded-lg bg-slate-50 p-4 text-sm text-slate-700" data-testid="case-reply-text">
          {last.text}
          <button onClick={() => { navigator.clipboard.writeText(last.text); toast.success("Copiado"); }} className="mt-2 flex items-center gap-1 text-xs font-bold text-amber-700"><Copy className="h-3 w-3" /> Copiar</button>
        </div>
      )}
    </Panel>
  );
}

function CaseHeader({ c }) {
  const a = c.analysis;
  return (
    <div className="flex flex-col gap-4 hero-rings relative overflow-hidden rounded-3xl bg-slate-900 p-6 text-white sm:p-8 md:flex-row md:items-center md:justify-between">
      <div>
        <div className="flex flex-wrap items-center gap-2"><PlatformBadge platform={c.platform} /><StatusBadge status={c.status} testid="case-status-badge" /><span className="text-xs text-slate-400">{fmtDate(c.created_at)}</span></div>
        <h1 className="mt-3 font-display text-3xl font-extrabold sm:text-4xl" data-testid="case-order-number">{caseTitle(c)}</h1>
        <p className="mt-1 text-sm text-white/60">{c.case_code}{c.order_number ? ` · Pedido ${c.order_number}` : ""}</p>
        <p className="mt-1 text-sm text-slate-300">{OCCURRENCES[c.occurrence_type]} · {DELIVERY[c.delivery_by]} · Lacre: {YESNO[c.sealed_package]} · Foto pré-coleta: {YESNO[c.photo_before_pickup]}</p>
      </div>
      {a?.forca != null && (
        <div className="flex items-center gap-4 rounded-2xl bg-white/5 p-4">
          <div className="rounded-full bg-white p-1"><ScoreRing pct={Math.round(a.forca * 10)} size={78} stroke={6} label="estimada" /></div>
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-amber-400">Força da contestação</p>
            <div className="mt-1 [&_.text-slate-900]:text-white [&_.text-slate-300]:text-white/25"><StrengthStars value={a.forca} size="text-lg" /></div>
            <p className="mt-1 text-[11px] text-slate-400">Força das evidências — não é probabilidade oficial de aprovação.</p>
          </div>
        </div>
      )}
    </div>
  );
}

function AnalysisColumn({ c, setC, limit }) {
  const a = c.analysis;
  const hasFacts = a.fatos_comprovados?.length > 0 || a.indicios?.length > 0 || a.nao_verificado?.length > 0;
  return (
    <div className="space-y-6 lg:col-span-2">
      <FillGuide platform={c.platform} occurrence={c.occurrence_type} motivo={c.chosen_motivo || a.motivo_recomendado} submotivo={c.chosen_submotivo || a.submotivo_recomendado} />
      <div className={`flex flex-col gap-1 rounded-xl p-5 text-white ${REC_CLS[a.recomendacao_final] || "bg-slate-700"}`} data-testid="final-recommendation">
        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest opacity-80"><Gavel className="h-4 w-4" /> Recomendação final</p>
        <p className="font-display text-2xl font-extrabold">{FINAL_REC[a.recomendacao_final] || a.recomendacao_final}</p>
        <p className="text-sm opacity-90">{a.justificativa_recomendacao}</p>
      </div>
      {a.alerta_seguranca_alimentar && (
        <div className="flex gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800" data-testid="food-safety-alert"><ShieldAlert className="h-5 w-5 shrink-0" />{a.alerta_seguranca_alimentar}</div>
      )}
      <Panel title="1. Análise do cliente" icon={FileSearch} testid="case-summary"><p className="text-slate-700">{a.resumo}</p>
        {a.categoria_corresponde && <p className="mt-3 text-sm text-slate-500"><b>Categoria da plataforma:</b> {a.categoria_corresponde}</p>}</Panel>
      {a.ponto_principal_defesa && <Panel title="2. Ponto principal da defesa" icon={Target} testid="main-defense-point" className="border-emerald-200 bg-emerald-50/40"><p className="text-slate-800">{a.ponto_principal_defesa}</p></Panel>}
      <Panel title="3. Contradições encontradas" icon={AlertTriangle}><List items={a.contradicoes} testid="contradictions-list" empty="Nenhuma contradição objetiva." /></Panel>
      <div className="grid gap-6 sm:grid-cols-2">
        <Panel title="4. Motivo e 5. Submotivo recomendados" icon={Target} testid="recommended-motivo">
          <p className="font-semibold text-slate-900">{a.motivo_recomendado}</p>
          {a.submotivo_recomendado && <><p className="mt-3 text-xs font-bold uppercase tracking-widest text-slate-400">Submotivo</p><p className="font-semibold text-slate-800" data-testid="recommended-submotivo">{a.submotivo_recomendado}</p></>}
          <p className="mt-3 text-sm text-slate-500">{a.justificativa_motivo}</p>
        </Panel>
        <Panel title="Responsabilidade provável" icon={Scale} testid="responsibility">
          <p className="font-display text-3xl font-extrabold text-slate-900">{RESPONSIBILITY[a.responsabilidade] || a.responsabilidade}</p>
          <p className="mt-2 text-sm text-slate-500">Problema classificado como: <b>{a.classificacao_problema}</b></p>
        </Panel>
      </div>
      <ContestEditor c={c} setC={setC} limit={limit} />
      <Panel title="Evidência principal" icon={FileCheck2} testid="main-evidence"><p className="text-slate-700">{a.evidencia_principal}</p></Panel>
      {hasFacts && (
        <div className="grid gap-6 sm:grid-cols-3" data-testid="facts-panel">
          <Panel title="Fatos comprovados"><List items={a.fatos_comprovados} empty="Nenhum." /></Panel>
          <Panel title="Indícios"><List items={a.indicios} empty="Nenhum." /></Panel>
          <Panel title="Não verificado"><List items={a.nao_verificado} empty="Nenhum." /></Panel>
        </div>
      )}
      <div className="grid gap-6 sm:grid-cols-2">
        <Panel title="8. Provas recomendadas" icon={FileCheck2}><List items={a.documentos_recomendados} testid="recommended-docs" /></Panel>
        <Panel title="9. Risco de negativa" icon={ShieldAlert} testid="denial-risk">{a.risco_negativa ? <p className="text-sm text-slate-700">{a.risco_negativa}</p> : <List items={a.pontos_fracos} testid="weak-points-list" />}</Panel>
      </div>
      <Panel title="Alegações do cliente" icon={ListChecks} testid="claims-panel">
        <div className="space-y-3">{(a.alegacoes || []).map((x) => (
          <div key={x.alegacao} className="rounded-lg border border-slate-200 p-3">
            <div className="flex items-start justify-between gap-3"><p className="font-semibold text-slate-800">{x.alegacao}</p><span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-bold ${ALEG_CLS[x.status] || ALEG_CLS.nao_comprovavel}`}>{x.status?.replace("_", " ")}</span></div>
            <p className="mt-1 text-sm text-slate-500">{x.analise}</p>
          </div>))}</div>
      </Panel>
    </div>
  );
}

function SideColumn({ c, setC, cats, busy, reanalyze }) {
  const a = c.analysis;
  return (
    <div className="space-y-6">
      <TrackingPanel c={c} setC={setC} cats={cats} />
      <Panel title="Evidências originais" testid="evidence-gallery">
        {c.evidences?.length ? <div className="grid grid-cols-2 gap-2">{c.evidences.map((e) => (
          <div key={e.file_id}><AuthImage fileId={e.file_id} className="aspect-square w-full rounded-lg object-cover" /><p className="mt-1 text-[11px] text-slate-500">{e.type}</p></div>))}</div>
          : <p className="text-sm text-slate-400">Nenhuma imagem anexada.</p>}
        {a.analise_evidencias?.length > 0 && <div className="mt-4 space-y-2">{a.analise_evidencias.map((x) => (
          <p key={`${x.evidencia}-${x.observacao}`} className={`rounded-md p-2 text-xs ${x.favoravel ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800"}`}><b>{x.evidencia}:</b> {x.observacao}</p>))}</div>}
      </Panel>
      <Panel title="Dados informados">
        <dl className="space-y-2 text-sm"><dt className="font-semibold text-slate-500">Comentário do cliente</dt><dd className="text-slate-800">{c.customer_comment || "—"}</dd>
          <dt className="font-semibold text-slate-500">Motivo da plataforma</dt><dd className="text-slate-800">{c.platform_reason || "—"}</dd>
          <dt className="font-semibold text-slate-500">Informações complementares</dt><dd className="whitespace-pre-line text-slate-800" data-testid="case-complementary-info">{c.complementary_info || "—"}</dd>
          <dt className="font-semibold text-slate-500">Explicação da loja</dt><dd className="text-slate-800">{c.store_explanation || "—"}</dd></dl>
        <button data-testid="reanalyze-btn" onClick={reanalyze} disabled={busy} className={`${btnGhost} mt-4 w-full`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Reanalisar caso</button>
      </Panel>
      <CaseReply c={c} setC={setC} />
    </div>
  );
}

function ReplyCaseView({ c, setC, limit }) {
  const a = c.analysis;
  return (
    <div className="grid gap-6 lg:grid-cols-3" data-testid="reply-case-view">
      <div className="space-y-6 lg:col-span-2">
        <Panel title="Comentário do cliente" icon={MessageSquareReply}>
          <SentimentChip value={a.sentimento} />
          <p className="mt-3 text-slate-700">{a.resumo}</p>
          {c.customer_comment && <p className="mt-3 rounded-lg bg-slate-50 p-3 text-sm italic text-slate-600">“{c.customer_comment}”</p>}
        </Panel>
        <ContestEditor c={c} setC={setC} limit={limit} styles={REPLY_TONES} title="Resposta pública ao cliente" regenLabel="Reescrever no tom" />
      </div>
      <Panel title="Print do comentário" testid="evidence-gallery">
        {c.evidences?.length ? <div className="grid grid-cols-2 gap-2">{c.evidences.map((e) => <AuthImage key={e.file_id} fileId={e.file_id} className="aspect-square w-full rounded-lg object-cover" />)}</div>
          : <p className="text-sm text-slate-400">Nenhuma imagem anexada.</p>}
      </Panel>
    </div>
  );
}

export default function CaseResult() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [cats, setCats] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [{ data: cs }, { data: conf }] = await Promise.all([api.get(`/cases/${id}`), api.get("/config")]);
        setC(cs); setCfg(conf);
        const { data: cl } = await api.get(`/categories?platform=${cs.platform}`);
        setCats(cl);
      } catch (e) { toast.error(errMsg(e)); }
    };
    load();
  }, [id]);

  const reanalyze = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/cases/${id}/analyze`); setC(data); toast.success("Análise atualizada"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!c || !cfg) return <div className="h-64 animate-pulse rounded-xl bg-slate-200" />;
  const limit = charLimit(cfg, c.platform, c.occurrence_type);

  return (
    <div className="fade-up space-y-6" data-testid="case-result-page">
      <div className="flex items-center justify-between gap-3">
        <Link to="/recursos" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-900"><ArrowLeft className="h-4 w-4" /> Histórico</Link>
        <DeleteCaseButton caseId={c.case_id} onDeleted={() => nav("/recursos")} />
      </div>
      <CaseHeader c={c} />
      {!c.analysis ? (
        <Panel title="Análise pendente" icon={FileSearch}>
          <p className="text-sm text-slate-500">Este caso ainda não foi analisado (ou a análise anterior falhou).</p>
          <button data-testid="run-analysis-btn" onClick={reanalyze} disabled={busy} className="ai-cta mt-4 flex h-14 items-center gap-2 rounded-xl px-8 font-display font-extrabold text-white">{busy && <Loader2 className="h-5 w-5 animate-spin" />} FORMULAR DEFESA</button>
        </Panel>
      ) : (
        c.occurrence_type === "resposta" ? <ReplyCaseView c={c} setC={setC} limit={limit} /> : (
          <div className="grid gap-6 lg:grid-cols-3">
            <AnalysisColumn c={c} setC={setC} limit={limit} />
            <SideColumn c={c} setC={setC} cats={cats} busy={busy} reanalyze={reanalyze} />
          </div>
        )
      )}
    </div>
  );
}
